import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AlertService, StorageService, ApprovalAnalyticsDashboardService } from '../../../_services';
import { DayOfWeekLabels } from '../../../_models';
import { fmtMoney, fmtCount, fmtRate, getTrendClass, isTrendNegative, getAbsTrend } from '../gateway-dashboard.helpers';
import {
  BankBinGroup,
  FilterOption,
  FilterOptionsResponse,
  GatewayDashboardFilters,
  GatewayReportField,
  OverviewCard,
  OverviewCardsResponse,
  DetailsRow,
  DetailsTableResponse,
} from '../gateway-summary';
import {
  ApprovalAnalyticsKpiKey,
  ApprovalKpiCard,
  ApprovalPerfChip,
  ApprovalPerfRow,
  AiInsight,
  DimensionOptionsResponse,
  ApprovalDimensionGroup,
  ApprovalDimension,
  approvalKpiCards,
  aiInsightsMock,
  PerfChipKeys,
  PerfChipAbbrLabels,
  dimensionStatusDeferred,
} from './approval-analytics.data';

@Component({
  selector: 'app-approval-analytics',
  templateUrl: './approval-analytics.component.html',
  styleUrls: ['./approval-analytics.component.scss'],
})
export class ApprovalAnalyticsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() filters: GatewayDashboardFilters = {};

  // ── KPI cards ──────────────────────────────────────────────────────────────
  kpiCards: ApprovalKpiCard[] = approvalKpiCards.map(card => ({ ...card }));
  kpiLoading: boolean = true;
  kpiNoData: boolean = false;

  // ── Performance Breakdown ──────────────────────────────────────────────────
  perfChips: ApprovalPerfChip[] = [];
  chipsLoading: boolean = true;
  activePerfChip: ApprovalPerfChip = null;
  perfTableRows: ApprovalPerfRow[] = [];
  perfLoading: boolean = true;
  perfNoData: boolean = false;

  // ── Issuer Bank/BIN picker ─────────────────────────────────────────────────
  reportField = GatewayReportField;
  bankBinGroups: BankBinGroup[] = [];
  selectedBanks: string[] = [];
  selectedBins: string[] = [];
  isBankPickerOpen: boolean = false;
  bankSearch: string = '';
  binSearch: string = '';
  openBinGroup: BankBinGroup = null;
  binFlyoutTop: number = 0;

  // ── AI Summary (not yet implemented — shows mock data for staff only) ──────
  isStaff: boolean = false;
  aiInsights: AiInsight[] = aiInsightsMock;
  aiLoading: boolean = false;
  aiNoData: boolean = false;

  protected _destroy$: Subject<boolean> = new Subject<boolean>();
  private isFirstChange: boolean = true;
  private perfTableSub: Subscription;
  private bankGroupsSub: Subscription;

  getTrendClass = getTrendClass;
  isTrendNegative = isTrendNegative;
  getAbsTrend = getAbsTrend;

  constructor(
    private approvalAnalyticsService: ApprovalAnalyticsDashboardService,
    private alertService: AlertService,
    private storageService: StorageService
  ) {}

  ngOnInit() {
    this.isStaff = this.isCurrentUserStaff();
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filters'] && !this.isFirstChange) {
      this.loadData();
    }
    this.isFirstChange = false;
  }

  ngOnDestroy() {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  // ── Data loading ────────────────────────────────────────────────────────────

  loadData() {
    this.closeBankPicker();
    this.selectedBanks = [];
    this.selectedBins = [];
    this.loadKpiCards();
    this.loadDimensionOptions();
    this.loadBankBinGroups();
  }

  loadKpiCards() {
    this.kpiLoading = true;
    this.kpiNoData = false;
    this.kpiCards = approvalKpiCards.map(card => ({ ...card }));

    this.approvalAnalyticsService
      .getOverviewCards(this.filters)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (response: OverviewCardsResponse) => {
          this.mapKpiCards(response);
        },
        () => {
          this.alertService.error('Failed to load KPI data.');
          this.kpiLoading = false;
        }
      );
  }

  loadDimensionOptions() {
    this.chipsLoading = true;
    this.perfLoading = true;
    this.perfNoData = false;

    this.approvalAnalyticsService
      .getDimensionOptions(this.filters)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (response: DimensionOptionsResponse) => {
          this.buildChips(response);
          this.chipsLoading = false;
          if (this.activePerfChip) {
            this.loadDetailsTable();
          } else {
            this.perfLoading = false;
            this.perfNoData = true;
          }
        },
        () => {
          this.alertService.error('Failed to load dimension options.');
          this.chipsLoading = false;
          this.perfLoading = false;
          this.perfNoData = true;
        }
      );
  }

  loadDetailsTable() {
    if (this.activePerfChip) {
      this.perfLoading = true;
      this.perfNoData = false;
      this.perfTableRows = [];

      if (this.perfTableSub) {
        this.perfTableSub.unsubscribe();
      }
      this.perfTableSub = this.approvalAnalyticsService
        .getDetailsTable(this.perfFilters, [this.activePerfChip.key])
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (response: DetailsTableResponse) => {
            this.mapDetailsTable(response);
          },
          () => {
            this.alertService.error('Failed to load performance breakdown.');
            this.perfLoading = false;
            this.perfNoData = true;
          }
        );
    }
  }

  loadBankBinGroups() {
    if (this.bankGroupsSub) {
      this.bankGroupsSub.unsubscribe();
    }
    this.bankGroupsSub = this.approvalAnalyticsService
      .getFilterOptions(this.filters)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (response: FilterOptionsResponse) => {
          let groups: BankBinGroup[] = [];
          if (response && response.filters) {
            response.filters.forEach((option: FilterOption) => {
              if (option.key === GatewayReportField.IssuerBankName && option.bin_groups) {
                groups = option.bin_groups;
              }
            });
          }
          this.bankBinGroups = groups;
        },
        () => {
          this.alertService.error('Failed to load banks.');
        }
      );
  }

  // ── Mapping ─────────────────────────────────────────────────────────────────

  mapKpiCards(response: OverviewCardsResponse) {
    this.kpiLoading = false;

    if (response && response.cards && response.cards.length) {
      const cardMap: { [key: string]: OverviewCard } = {};
      response.cards.forEach((card: OverviewCard) => {
        cardMap[card.key] = card;
      });

      const transCard = cardMap[ApprovalAnalyticsKpiKey.Transactions];
      if (transCard) {
        this.kpiCards[0].value = fmtCount(transCard.value, '--');
        this.kpiCards[0].trend_pct = transCard.trend_pct !== undefined ? transCard.trend_pct : null;
      }

      const approvalsCard = cardMap[ApprovalAnalyticsKpiKey.Approvals];
      if (approvalsCard) {
        this.kpiCards[1].value = fmtCount(approvalsCard.value, '--');
        this.kpiCards[1].trend_pct = approvalsCard.trend_pct !== undefined ? approvalsCard.trend_pct : null;
      }

      const rateCard = cardMap[ApprovalAnalyticsKpiKey.ApprovalRate];
      if (rateCard) {
        this.kpiCards[2].value = fmtRate(rateCard.value, 1, '--');
        this.kpiCards[2].trend_pct = rateCard.trend_pct !== undefined ? rateCard.trend_pct : null;
      }

      const revenueCard = cardMap[ApprovalAnalyticsKpiKey.GrossRevenue];
      if (revenueCard) {
        this.kpiCards[3].value = fmtMoney(revenueCard.value);
        this.kpiCards[3].trend_pct = revenueCard.trend_pct !== undefined ? revenueCard.trend_pct : null;
      }
    }
  }

  buildChips(response: DimensionOptionsResponse) {
    // Build a flat lookup of all dimensions returned by the API
    const dimMap: { [key: string]: ApprovalDimension } = {};
    if (response && response.groups) {
      response.groups.forEach((group: ApprovalDimensionGroup) => {
        group.dimensions.forEach((dim: ApprovalDimension) => {
          dimMap[dim.key] = dim;
        });
      });
    }

    // Build chips in the fixed Figma order, only for keys present in the API response
    const chips: ApprovalPerfChip[] = [];
    PerfChipKeys.forEach((key: string) => {
      const dim = dimMap[key];
      if (dim) {
        chips.push({
          key: dim.key,
          label: PerfChipAbbrLabels[dim.key] || dim.label,
          isDeferred: dim.status === dimensionStatusDeferred,
          reason: dim.reason,
        });
      }
    });

    this.perfChips = chips;

    // Default active chip: first non-deferred in the ordered list
    let defaultChip: ApprovalPerfChip = null;
    for (let i = 0; i < chips.length; i++) {
      if (!chips[i].isDeferred) {
        defaultChip = chips[i];
        break;
      }
    }
    this.activePerfChip = defaultChip;
  }

  mapDetailsTable(response: DetailsTableResponse) {
    this.perfLoading = false;

    if (response && response.results && response.results.length) {
      this.perfNoData = false;
      this.perfTableRows = response.results.map((row: DetailsRow) => this.toPerfRow(row));
    } else {
      this.perfNoData = true;
    }
  }

  isCurrentUserStaff(): boolean {
    const user = this.storageService.get('currentUser');
    let isStaff = false;
    if (user && user.is_staff) {
      isStaff = true;
    }
    return isStaff;
  }

  navigateToReport() {
    if (this.activePerfChip) {
      this.storageService.set('gateway_dashboard_nav_filters', {
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        compare_start_date: this.filters.compare_start_date,
        compare_end_date: this.filters.compare_end_date,
        merchant_id: this.filters.merchant_id,
        campaign_id: this.filters.campaign_id,
        order_type: this.filters.order_type,
        count_type: this.filters.count_type,
      });
      window.open('/gateway-performance-report-v3/dim/' + this.activePerfChip.key, '_blank');
    }
  }

  setActiveChip(chip: ApprovalPerfChip) {
    if (!chip.isDeferred) {
      if (chip.key === GatewayReportField.IssuerBankName && !(this.isBankChipActive && this.isBankPickerOpen)) {
        this.isBankPickerOpen = true;
      } else {
        this.closeBankPicker();
      }
      this.activePerfChip = chip;
      this.loadDetailsTable();
    }
  }

  // ── Issuer Bank/BIN picker ──────────────────────────────────────────────────

  get isBankChipActive(): boolean {
    return !!this.activePerfChip && this.activePerfChip.key === GatewayReportField.IssuerBankName;
  }

  get perfFilters(): GatewayDashboardFilters {
    let perfFilters = this.filters;
    if (this.isBankChipActive) {
      perfFilters = { ...this.filters, issuer_bank_name: this.selectedBanks, card_bin: this.selectedBins };
    }
    return perfFilters;
  }

  get visibleBankGroups(): BankBinGroup[] {
    const term = this.bankSearch.toLowerCase();
    return this.bankBinGroups.filter((group: BankBinGroup) => group.name.toLowerCase().indexOf(term) > -1);
  }

  get visibleBins(): string[] {
    let bins: string[] = [];
    if (this.openBinGroup) {
      const term = this.binSearch.toLowerCase();
      bins = this.openBinGroup.bins.filter((bin: string) => bin.toLowerCase().indexOf(term) > -1);
    }
    return bins;
  }

  toggleBank(name: string) {
    this.selectedBanks = this.toggleValue(this.selectedBanks, name);
    this.loadDetailsTable();
  }

  toggleBin(bin: string) {
    this.selectedBins = this.toggleValue(this.selectedBins, bin);
    this.loadDetailsTable();
  }

  toggleAllBanks() {
    let banks: string[] = [];
    if (!this.isAllBanksSelected()) {
      banks = this.bankBinGroups.map((group: BankBinGroup) => group.name);
    }
    this.selectedBanks = banks;
    this.loadDetailsTable();
  }

  isAllBanksSelected(): boolean {
    return this.bankBinGroups.length > 0
      && this.bankBinGroups.every((group: BankBinGroup) => this.selectedBanks.indexOf(group.name) > -1);
  }

  openBinFlyout(group: BankBinGroup, event: MouseEvent) {
    if (this.openBinGroup === group) {
      this.openBinGroup = null;
    } else {
      this.openBinGroup = group;
      this.binSearch = '';
      const rowEl = (event.currentTarget as HTMLElement).closest('.aa-picker-row');
      const pickerEl = rowEl ? rowEl.closest('.aa-bank-picker') : null;
      if (pickerEl) {
        this.binFlyoutTop = rowEl.getBoundingClientRect().top - pickerEl.getBoundingClientRect().top;
      }
    }
  }

  closeBankPicker() {
    this.isBankPickerOpen = false;
    this.openBinGroup = null;
  }

  toggleBankRow(row: ApprovalPerfRow): void {
    if (!row.loading) {
      if (row.children) {
        row.expanded = !row.expanded && row.children.length > 0;
      } else {
        row.loading = true;
        this.approvalAnalyticsService
          .getDetailsTable({ ...this.perfFilters, issuer_bank_name: [row.dimension] }, [GatewayReportField.CardBin])
          .pipe(takeUntil(this._destroy$))
          .subscribe(
            (response: DetailsTableResponse) => {
              const results = (response && response.results) || [];
              row.loading = false;
              row.children = results.map((child: DetailsRow) => this.toPerfRow(child));
              row.expanded = row.children.length > 0;
            },
            () => {
              row.loading = false;
              this.alertService.error('Failed to load BIN breakdown.');
            }
          );
      }
    }
  }

  private toggleValue(list: string[], value: string): string[] {
    let next = [...list, value];
    if (list.indexOf(value) > -1) {
      next = list.filter((item: string) => item !== value);
    }
    return next;
  }

  private toPerfRow(row: DetailsRow): ApprovalPerfRow {
    const isDayOfWeek = this.activePerfChip && this.activePerfChip.key === GatewayReportField.DayOfWeek;
    let dimensionLabel = String(row.dimension);
    if (isDayOfWeek) {
      const dayLabel = DayOfWeekLabels[Number(row.dimension)];
      if (dayLabel) {
        dimensionLabel = dayLabel;
      }
    }
    const displayRow: ApprovalPerfRow = {
      dimension: dimensionLabel,
      attempts: fmtCount(row.transactions, '--'),
      approvals: fmtCount(row.approvals, '--'),
      approvalRate: fmtRate(row.approval_rate, 1, '--'),
      approvedRevenue: fmtMoney(row.approved),
    };
    return displayRow;
  }

}
